import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useStore } from "../../../store/globalState";
import { FirstSendWarning } from "../../../components/first-send/FirstSendWarning";
import { useFirstSendWarning } from "../../../hooks/useFirstSendWarning";
import { shouldPromptPhone } from "../../../utils/phoneGate";
import { ensureSmsConsent } from "../../../components/notifications/smsConsent";
import { useSessionStore } from "../../../store/useSessionStore";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AuraLayout,
  BankIcon,
  BankV2Icon,
  Button,
  CashSpecialIcon,
  ExchangeV2Icon,
  Layout,
  WalletIcon,
} from "lola-framework-ui-test";
import { APP_ROUTES, TITLE_COLOR, flavorClassName } from "../../../constants";
import { FlagCircle } from "../../../components/FlagCircle";
import { QuoteInfo } from "./components/QuoteInfo";
import { amountFormatter } from "../../../utils";
import { formatBankAccountLabel } from "../../../utils/bankAccountLabel";
import { PayoutInfo } from "./components/PayoutInfo";
import { Popup } from "../../../components/Popup";
import { SelectPaymentMethodSlot } from "./components/SelectPaymentMethodSlot";
import { DestinationAccountInfo } from "./components/DestinationAccountInfo";
import { paymentType } from "../../../interfaces/payment";
import masterCardLogo from "../../../assets/master_card_icon.svg";
import visaLogo from "../../../assets/visa_color_icon.svg";
import { useTransferPricing } from "../../../hooks/useTransferPricing";
import { getEvents, safeCapture } from "../../../analytics";
import { quoteMatchesSelection } from "../../../utils/quoteMatchesSelection";
import { useQuoteLoadingTimedOut } from "../../../hooks/useQuoteLoadingTimedOut";
import { BodyCopy } from "../../../components/BodyCopy";
import "../../../styles/transfer.flavorc.style.css";
import { checkCipReadiness, getAllPaymentMethods, getWidgetApiError, isDeadLinkError } from "../../../services/api";
import "../../../styles/transfer.flavorb.style.css";
import { categoryFromFundingMethod } from "../../../utils/catalog";
import { ScrollFadeIndicator } from "../../../components/ScrollFadeIndicator";
import { Navbar } from "../../../components/Navbar";


export const TransferPage = memo(() => {
  const { t, i18n } = useTranslation();
  const {
    theme,
    amountState,
    paymentState,
    setPaymentState,
    setQuoteState,
    cardList,
  } = useStore();
  const { session } = useSessionStore();
  const userStatus = session.data?.userStatus;
  const canSend = !userStatus?.blocked && !userStatus?.suspended;
  const { amount: amountRaw } = amountState;
  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, title, footerColor, bodyCopy } =
    colorConfig;
  const navigate = useNavigate();
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [showDestinationPopup, setShowDestinationPopup] = useState(false);
  const [shakePayment, setShakePayment] = useState(false);
  const [cipLoading, setCipLoading] = useState(false);

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("route_amount"));
    safeCapture(getEvents().SCREEN_VIEW("summary"));
  }, []);

  // Pricing: pre-quote (scoped to the claim's fixed payout) + binding quote.
  const {
    route: { sourceCountry, destinationCountry, sourceCurrency, destinationCurrency },
    availableCategories,
    quote,
    quoteLoading,
    quoteError,
    refetchQuote,
    preQuoteLoading,
    economics,
    amount: pricedAmount,
    anchor: amountAnchor,
    selectedFundingMethod,
    capabilityKey,
  } = useTransferPricing();
  const amount = amountRaw;
  const quoteMatches = quoteMatchesSelection(quote, quoteLoading, {
    fundingMethod: selectedFundingMethod,
    capabilityKey,
  });
  const quoteLoadingTimedOut = useQuoteLoadingTimedOut(quoteLoading);
  const quoteFailed = Boolean(quoteError) || quoteLoadingTimedOut;
  const skuMissing =
    !preQuoteLoading && !!paymentState.payment && !capabilityKey;
  const firstSend = useFirstSendWarning({
    linkId: session.data?.linkId,
    sendAmount: economics?.sendAmount ?? quote?.sendAmount ?? null,
    sourceCurrency,
    fundingMethod: selectedFundingMethod,
  });
  const blockForPricing =
    Boolean(pricedAmount) &&
    Boolean(paymentState.payment) &&
    !quoteMatches &&
    !quoteFailed &&
    !skuMissing;
  const showQuoteLoading =
    (quoteLoading || blockForPricing) && !quoteFailed && !skuMissing;

  // Auto-select last used funding method on mount
  useEffect(() => {
    if (paymentState.payment) return;
    const linkId = session.data?.linkId;
    if (!linkId) return;

    getAllPaymentMethods(linkId)
      .then((methods) => {
        if (paymentState.payment) return;
        const lastUsed = methods.lastUsedFunding;
        if (!lastUsed) return;

        const lastCategory = categoryFromFundingMethod(lastUsed.method);

        if (lastCategory === "CASH" && availableCategories.has("CASH")) {
          setPaymentState({
            payment: {
              cardId: null,
              type: paymentType.CASH,
              brand: t("payment.cashDeposit"),
            },
          });
        } else if (
          lastCategory === "CARD" &&
          lastUsed.paymentMethodId &&
          availableCategories.has("CARD")
        ) {
          const card = methods.cards.find(
            (c) => c.enabled && c.paymentMethodId === lastUsed.paymentMethodId
          );
          if (card) {
            setPaymentState({
              payment: {
                cardId: card.paymentMethodId,
                type: paymentType.CARD,
                brand: `${card.brand}-${card.last4}`,
              },
            });
          }
        }
      })
      .catch(() => {});
  }, [session.data?.linkId, cardList, availableCategories]);

  useEffect(() => {
    if (quoteError) {
      navigate(APP_ROUTES.SERVICE_UNAVAILABLE_PAGE, { replace: true });
    }
  }, [quoteError, navigate]);

  // Handle click on confirm button - check CIP readiness before proceeding
  const handleConfirmClick = useCallback(async () => {
    safeCapture(getEvents().CONTINUE_CLICK);
    if (!canSend) {
      navigate(APP_ROUTES.TRANSACTIONS_PAGE, { replace: true });
      return;
    }
    if (!paymentState.payment) {
      setShakePayment(true);
      setTimeout(() => setShakePayment(false), 500);
      return;
    }
    if (!amount || !quoteMatches || !quote) {
      return;
    }

    const linkId = session.data?.linkId;
    if (!linkId) {
      console.error("[TransferPage] No linkId found in session");
      return;
    }

    // DEBUG: Log quote info before storing
    const expiresAt = new Date(quote.expiresAt).getTime();
    const now = Date.now();
    const remainingMs = expiresAt - now;
    console.log("[TransferPage] Storing quote before navigation:", {
      quoteId: quote.quoteId,
      expiresAt: quote.expiresAt,
      remainingSeconds: Math.floor(remainingMs / 1000),
      isExpired: remainingMs <= 0,
      quoteLoading,
    });

    // Store quote in global state before navigating
    setQuoteState(quote);

    // Check CIP readiness before proceeding to transfer
    try {
      setCipLoading(true);
      console.log("[TransferPage] Checking CIP readiness...");
      const cipResult = await checkCipReadiness(linkId);
      console.log("[TransferPage] CIP readiness result:", cipResult);

      if (cipResult.shouldRunCip && cipResult.cipTicketId) {
        // User needs to complete CIP - navigate to embedded Kentucky SDK page
        console.log(
          "[TransferPage] CIP required, navigating to CIP page with ticketId:",
          cipResult.cipTicketId
        );
        navigate(APP_ROUTES.CIP_PAGE, {
          replace: true,
          state: { cipTicketId: cipResult.cipTicketId },
        });
        return;
      }

      const prompt = shouldPromptPhone(session.data, linkId);
      if (prompt) {
        navigate(APP_ROUTES.PHONE_PAGE, {
          replace: true,
          state: { next: APP_ROUTES.SENDING_MONEY_PAGE },
        });
        return;
      }
      await ensureSmsConsent(linkId, i18n.language?.slice(0, 2) || "en");
      navigate(APP_ROUTES.SENDING_MONEY_PAGE, {
        replace: true,
        state: { next: APP_ROUTES.SENDING_MONEY_PAGE },
      });
    } catch (err) {
      console.error("[TransferPage] CIP readiness check failed:", err);
      // Link expired / revoked: the dead-link handler already routed to the
      // screen that explains why the link stopped working.
      if (isDeadLinkError(err)) {
        return;
      }
      const apiCode = getWidgetApiError(err)?.code;
      if (apiCode === "USER_BLOCKED" || apiCode === "USER_SUSPENDED") {
        navigate(APP_ROUTES.ERROR_PAGE, {
          replace: true,
          state: {
            title: t("error.transfersDisabledTitle"),
            subTitle: null,
            message: t("error.transfersDisabledMessage"),
            btnText: t("common.needHelp"),
            isOut: false,
            pageUrl: APP_ROUTES.SUPPORT_PAGE,
          },
        });
        return;
      }
      // On error, navigate to error page
      navigate(APP_ROUTES.ERROR_PAGE, {
        state: {
          title: t("transfer.error"),
          subTitle: t("transfer.errorSubtitle", "Something went wrong"),
          message:
            err instanceof Error
              ? err.message
              : "Failed to check CIP readiness",
          btnText: t("transfer.tryAgain", "Try again"),
          isOut: false,
          pageUrl: APP_ROUTES.TRANSFER_PAGE,
        },
      });
    } finally {
      setCipLoading(false);
    }
  }, [
    canSend,
    paymentState.payment,
    amount,
    quote,
    quoteMatches,
    session.data?.linkId,
    setQuoteState,
    navigate,
    quoteLoading,
    t,
    i18n,
  ]);

  // Display economics: binding quote when available, else the pre-quote estimate.
  const exchangeRate = economics?.exchangeRate ?? 0;
  const fee = economics?.fee ?? 0;

  // Derived opposite-leg displays for the non-pinned QuoteInfo input.
  const receiveFormatted = useMemo(() => {
    if (quoteLoading) return "...";
    if (economics?.receiveAmount) {
      return amountFormatter(economics.receiveAmount, destinationCurrency, {
        country: "en-US",
      });
    }
    return "";
  }, [economics, quoteLoading, destinationCurrency]);

  const sendFormatted = useMemo(() => {
    if (quoteLoading) return "...";
    if (economics?.sendAmount != null) {
      return amountFormatter(economics.sendAmount, sourceCurrency, {
        country: "en-US",
      });
    }
    return "";
  }, [economics, quoteLoading, sourceCurrency]);

  const totalPayment = useMemo(() => {
    if (economics?.totalCost) {
      return amountFormatter(economics.totalCost);
    }
    if (amount) {
      return amountFormatter(parseFloat(amount) + fee);
    }
    return "0";
  }, [economics, amount, fee]);

  const feeDisplay = useMemo(() => {
    return `$${fee.toFixed(2)}`;
  }, [fee]);

  const pickIcon = useCallback((type: paymentType, brand: string) => {
    switch (type) {
      case "CARD": {
        let finalIcon = null;
        if (brand.toLocaleUpperCase().includes("VISA")) finalIcon = visaLogo;
        if (brand.toLocaleUpperCase().includes("MASTERCARD"))
          finalIcon = masterCardLogo;

        return {
          brand,
          icon: <img src={finalIcon ?? ""} alt={`${brand} logo`} />,
        };
      }
      case "BANK":
        return {
          brand,
          icon: (
            <BankIcon
              className="flavor-c-icon"
              colors={[footerColor, footerColor]}
              size={34}
            />
          ),
        };
      case "CASH":
        return {
          brand,
          icon: (
            <CashSpecialIcon
              className="flavor-c-icon"
              colors={[footerColor, footerColor]}
              size={34}
            />
          ),
        };

      default:
        return {
          brand,
          icon: (
            <WalletIcon
              className="flavor-c-icon"
              colors={[footerColor, footerColor]}
              size={34}
            />
          ),
        };
    }
  }, []);

  const paymentSelected = useMemo(
    () =>
      paymentState.payment
        ? pickIcon(paymentState.payment.type, paymentState.payment.brand)
        : {
            brand: t("transfer.selectPaymentMethod"),
            icon: (
              <WalletIcon
                className="flavor-c-icon"
                colors={[footerColor, footerColor]}
                size={34}
              />
            ),
          },
    [paymentState, pickIcon, t]
  );

  const payoutDisplay = useMemo(() => {
    const pm = session.data?.userData?.paymentMethod;
    if (!pm) {
      return {
        subtitle: t("transfer.bankAccount", { last4: "****" }),
        icon: <BankV2Icon size={24} />,
      };
    }
    if (pm.type === "bank_account" && pm.bankAccount) {
      const digits = (pm.bankAccount.accountNumber ?? "")
        .replace(/\D/g, "")
        .slice(-4);
      const label =
        formatBankAccountLabel(
          pm.bankAccount.bankName,
          pm.bankAccount.accountNumber,
        ) ??
        (digits
          ? t("transfer.bankAccount", { last4: digits })
          : pm.bankAccount.accountNumber);
      return { subtitle: label, icon: <BankV2Icon size={24} /> };
    }
    if (pm.type === "card" && pm.card) {
      const brand = pm.card.brand?.toUpperCase() || "CARD";
      return {
        // Card label parity: BRAND-0000.
        subtitle: `${brand}-${pm.card.last4}`,
        icon: <WalletIcon size={24} />,
      };
    }
    if (pm.type === "cash") {
      const label =
        pm.cash?.provider || t("transfer.cashPickup", "Cash pickup");
      return { subtitle: label, icon: <CashSpecialIcon size={24} /> };
    }
    return {
      subtitle: t("transfer.bankAccount", { last4: "****" }),
      icon: <BankV2Icon size={24} />,
    };
  }, [session.data?.userData?.paymentMethod, t]);

  if (!canSend) {
    navigate(APP_ROUTES.TRANSACTIONS_PAGE, { replace: true });
    return null;
  }

  return (
    <>
      <AuraLayout
        colorConfig={colorConfig}
        className={`scrollable white-view-background ${flavorClassName}`}
      >
        <Layout.Header>
          <Navbar
            color={title}
            title={t("transfer.title")}
            onBackClick={() => navigate(-1)}
          />
        </Layout.Header>
        <Layout.Content className="transfer-section flavor-c" isOverflowauto>
          <ScrollFadeIndicator color={colorConfig.bodyCopy} />
          <section>
            <section className="estimated-quote-section">
              <section className="quote-container">
                <QuoteInfo
                  label={t("transfer.from")}
                  currency={sourceCurrency}
                  mode="input"
                  side="send"
                  derivedAmount={
                    amountAnchor === "receive" ? sendFormatted || undefined : undefined
                  }
                  icon={<FlagCircle iso2={sourceCountry.toLowerCase()} />}
                />
                <ExchangeV2Icon
                  size={32}
                  className="exchange-quote-info flavor-c-icon"
                  colors={["#1dafa1", "#1dafa1"]}
                />
                <QuoteInfo
                  label={t("transfer.to")}
                  currency={destinationCurrency}
                  sourceCurrency={sourceCurrency}
                  mode="input"
                  side="receive"
                  derivedAmount={
                    amountAnchor === "send" ? receiveFormatted || undefined : undefined
                  }
                  icon={<FlagCircle iso2={destinationCountry.toLowerCase()} />}
                  exchangeRate={exchangeRate}
                />
              </section>
            </section>
            <section className="payout-container payout-container-v2">
              <PayoutInfo
                title={t("transfer.payingWith")}
                subtitle={paymentSelected.brand}
                icon={paymentSelected.icon}
                color={TITLE_COLOR}
                onChange={() => setShowPaymentPopup(true)}
                className={`${
                  !paymentState.payment ? "payment-required" : ""
                } ${shakePayment ? "shake" : ""}`.trim()}
              />
              <PayoutInfo
                title={t("transfer.receiveIn")}
                subtitle={payoutDisplay.subtitle}
                icon={payoutDisplay.icon}
                color={TITLE_COLOR}
                onChange={() => setShowDestinationPopup(true)}
              />
              {skuMissing ? (
                <BodyCopy className="payment-error-text" role="alert">
                  {t("transfer.paymentMethodUnavailable")}
                </BodyCopy>
              ) : null}
              {quoteFailed && !skuMissing ? (
                <>
                  <BodyCopy className="payment-error-text" role="alert">
                    {t("transfer.quoteError")}
                  </BodyCopy>
                  <Button
                    variant="cancel"
                    size="small"
                    color={bodyCopy}
                    onClick={() => {
                      void refetchQuote();
                    }}
                  >
                    {t("transfer.quoteRetry")}
                  </Button>
                </>
              ) : null}
              {firstSend.visible ? (
                <FirstSendWarning title={firstSend.title} body={firstSend.body} />
              ) : null}
              <p className="footer-text muted-foreground text-centered transfer-timing-note">
                <b>{t("transfer.instantTransfer")}</b>
                {" "}
                <span className="transfer-timing-note-rest">
                  {t("transfer.mayTakeFew")}
                </span>
              </p>
            </section>
          </section>
        </Layout.Content>
        <Layout.Footer className="flavor-b">
          <section className="footer-section">
            <section className="footer-summary summary-left gradient-card glass">
              <p className="footer-text foreground footer-summary-title">
                <b>{t("transfer.detail")}</b>
              </p>
              <p className="footer-text foreground">
                {t("transfer.fee", {
                  amount: feeDisplay,
                  currency: sourceCurrency,
                })}
              </p>
              <p className="footer-text foreground">
                {t("transfer.totalPayment", {
                  amount: `$${totalPayment}`,
                  currency: sourceCurrency,
                })}
              </p>
            </section>
            <div
              className={`button-wrapper ${
                (amount && paymentState.payment && quoteMatches) || cipLoading
                  ? "rainbow-ready"
                  : ""
              }`}
            >
              <Button
                size="small"
                background={backgroundBtn}
                color={textColorBtn}
                disabled={cipLoading || blockForPricing || quoteFailed}
                onClick={handleConfirmClick}
              >
                {cipLoading
                  ? t("transfer.verifying", "Verifying...")
                  : showQuoteLoading
                  ? t("transfer.loadingQuote", "Loading...")
                  : t("transfer.confirmTransfer")}
              </Button>
            </div>
          </section>
        </Layout.Footer>
      </AuraLayout>
      <Popup
        visible={showPaymentPopup}
        onClose={() => setShowPaymentPopup(false)}
      >
        <SelectPaymentMethodSlot onClose={() => setShowPaymentPopup(false)} />
      </Popup>
      <Popup
        visible={showDestinationPopup}
        onClose={() => setShowDestinationPopup(false)}
      >
        <DestinationAccountInfo
          onClose={() => setShowDestinationPopup(false)}
        />
      </Popup>
    </>
  );
});
